import React, { useMemo } from 'react';
import {
  ActivityFeed,
  ActivityFeedParams,
  useActivityFeed,
} from 'src/graphql/entities/activity-feed/hooks/use-activity-feed';
import { UserGroupRules } from 'src/graphql/entities/group-rules/group-rules.types';
import { ChatActivityMessage } from 'src/pages/Conversations/Chat/ChatMessage/chat-activity-message';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import {
  ChildStatus,
  MessageTypeId,
  MessageTypeRegistration,
  TimelineEntry,
} from 'src/pages/Conversations/Chat/registry/types';
import {
  DEFAULT_TIMEOUT_MS,
  useReadinessWithTimeout,
} from 'src/pages/Conversations/Chat/registry/use-readiness-with-timeout';
import { useGlobalStore } from 'src/store';

function useActivityFeedData() {
  const conversationId = useChatCommonData((it) => it.conversationId);
  const partnerId = useChatCommonData((it) => it.partnerId);
  const enabledActivityFeed = useChatCommonData(
    (it) => it.groupRules[UserGroupRules.AccessToActivityFeedInConversations],
  );
  const { conversations: showConversationActivities } = useGlobalStore.use.activityFeed();

  const skip = !showConversationActivities || !enabledActivityFeed;

  const activityFeedParams = useMemo<ActivityFeedParams>(
    () => ({
      identityIds: [conversationId],
      partnerId: Number(partnerId),
      entityEvents: [
        { entityName: 'Conversation' },
        { entityName: 'ConversationToAddress' },
        { entityName: 'Behavior', operation: 'execute' },
        { entityName: 'Macro', operation: 'execute' },
      ],
      // Activities a macro/rule caused render nested inside their parent, not as rows of their own.
      // They are not fetched with the feed: a run loads what it caused when the user expands it.
      rootsOnly: true,
    }),
    [conversationId, partnerId],
  );

  const labelsActivityFeedParams = useMemo<ActivityFeedParams>(
    () => ({
      identityIds: [conversationId],
      partnerId: Number(partnerId),
      entityEvents: [{ entityName: 'LabelAssignment' }],
      isRoot: false,
      referenceName: 'Conversation',
      // Labels a macro/rule added belong inside that run, so they must be excluded here too.
      rootsOnly: true,
    }),
    [conversationId, partnerId],
  );

  const { records: activityFeed, loading: loadingFeed } = useActivityFeed(
    activityFeedParams,
    skip,
  );
  const { records: labelsActivityFeed, loading: loadingLabels } = useActivityFeed(
    labelsActivityFeedParams,
    skip,
  );

  return { skip, activityFeed, labelsActivityFeed, loadingFeed, loadingLabels };
}

function useItems(): TimelineEntry<ActivityFeed>[] {
  const { skip, activityFeed, labelsActivityFeed } = useActivityFeedData();

  return useMemo<TimelineEntry<ActivityFeed>[]>(() => {
    if (skip) return [];
    const combined = (activityFeed ?? []).concat(labelsActivityFeed ?? []);
    return combined.map((activity) => ({
      type: MessageTypeId.Activity,
      timestamp: activity.timestamp,
      item: activity,
    }));
  }, [activityFeed, labelsActivityFeed, skip]);
}

function useReadiness(): ChildStatus {
  const { skip, activityFeed, labelsActivityFeed, loadingFeed, loadingLabels } =
    useActivityFeedData();

  const loading = loadingFeed || loadingLabels;
  const hasData = (activityFeed?.length ?? 0) > 0 || (labelsActivityFeed?.length ?? 0) > 0;

  // useActivityFeed does not expose an `error` field, so query
  // failures will surface as Loading → Timeout after DEFAULT_TIMEOUT_MS rather
  // than entering ChildStatus.Error immediately.
  const status = useReadinessWithTimeout({ loading, hasData, timeoutMs: DEFAULT_TIMEOUT_MS });

  return skip ? ChildStatus.Ready : status;
}

export const activityRegistration: MessageTypeRegistration<ActivityFeed> = {
  id: MessageTypeId.Activity,
  Row: ({ item }): React.JSX.Element => <ChatActivityMessage activity={item} />,
  getKey: (item) => item.id,
  useItems,
  useReadiness,
  blockingForInitialRender: false,
  timeoutMs: DEFAULT_TIMEOUT_MS,
};

export default activityRegistration;
